import React, { useState, useEffect, useCallback } from 'react';
import { X, Navigation, MessageSquare, Clock, MapPin, ChevronRight, Settings, Bell, BellOff } from 'lucide-react';
import { supabase } from './supabase';
import { supabaseData } from './supabaseData';
import { sendSMS, replaceTemplateVariables } from './smsUtils';

const SmartAssistant = ({ companyId, user, jobs = [], companyName }) => {
  const [showModal, setShowModal] = useState(false);
  const [currentJob, setCurrentJob] = useState(null);
  const [nextJob, setNextJob] = useState(null);
  const [driveTime, setDriveTime] = useState(null);
  const [isEnabled, setIsEnabled] = useState(true);
  const [dismissed, setDismissed] = useState(new Set());
  const [sendingText, setSendingText] = useState(false);
  const [textSent, setTextSent] = useState(false);

  // Load enabled setting
  useEffect(() => {
    const loadSettings = async () => {
      if (!companyId) return;
      const { data } = await supabaseData
        .from('companies')
        .select('smart_assistant_enabled')
        .eq('id', companyId)
        .single();
      if (data) {
        setIsEnabled(data.smart_assistant_enabled !== false);
      }
    };
    loadSettings();
  }, [companyId]);

  // Calculate drive time using Google Maps Distance Matrix API (or estimate)
  const estimateDriveTime = useCallback((fromAddress, toAddress) => {
    // For now, return a rough estimate. In production, use Google Maps API
    // Average drive time estimate: 15-20 minutes between jobs
    return { text: '~15-20 min', value: 1000 };
  }, []);

  // Check for jobs nearing end time
  useEffect(() => {
    if (!isEnabled || !jobs.length) return;

    const checkJobs = () => {
      const now = new Date();
      const today = now.toISOString().split('T')[0];

      // Get today's jobs sorted by scheduled time
      const todayJobs = jobs
        .filter(job => {
          const jobDate = job.scheduled_date?.split('T')[0];
          return jobDate === today && job.status !== 'completed' && job.status !== 'cancelled';
        })
        .sort((a, b) => {
          const timeA = a.scheduled_time || '00:00';
          const timeB = b.scheduled_time || '00:00';
          return timeA.localeCompare(timeB);
        });

      // Find the current job (in-progress or next upcoming)
      let activeJob = todayJobs.find(j => j.status === 'in-progress');

      if (!activeJob) {
        // Find next upcoming job
        activeJob = todayJobs.find(job => {
          if (!job.scheduled_time) return false;
          const [hours, minutes] = job.scheduled_time.split(':').map(Number);
          const jobTime = new Date(now);
          jobTime.setHours(hours, minutes, 0, 0);
          return jobTime > now;
        });
      }

      if (!activeJob) return;

      // Calculate end time (scheduled_time + estimated duration, default 1 hour)
      const duration = activeJob.estimated_duration || 60; // minutes
      const [hours, minutes] = (activeJob.scheduled_time || '09:00').split(':').map(Number);
      const endTime = new Date(now);
      endTime.setHours(hours, minutes + duration, 0, 0);

      // Check if within 15 minutes of end time
      const timeUntilEnd = (endTime - now) / (1000 * 60); // minutes

      if (timeUntilEnd <= 15 && timeUntilEnd > -5 && !dismissed.has(activeJob.id)) {
        // Find next job
        const currentIndex = todayJobs.findIndex(j => j.id === activeJob.id);
        const upcoming = todayJobs[currentIndex + 1];

        if (upcoming) {
          setCurrentJob(activeJob);
          setNextJob(upcoming);

          // Estimate drive time
          if (activeJob.customers?.address && upcoming.customers?.address) {
            const estimate = estimateDriveTime(activeJob.customers.address, upcoming.customers.address);
            setDriveTime(estimate);
          }

          setShowModal(true);
          setTextSent(false);
        }
      }
    };

    // Check immediately and every minute
    checkJobs();
    const interval = setInterval(checkJobs, 60000);

    return () => clearInterval(interval);
  }, [isEnabled, jobs, dismissed, estimateDriveTime]);

  const handleDismiss = () => {
    if (currentJob) {
      setDismissed(prev => new Set([...prev, currentJob.id]));
    }
    setShowModal(false);
  };

  const handleQuickText = async () => {
    if (!nextJob?.customers?.phone) return;

    setSendingText(true);
    try {
      const firstName = nextJob.customers.name?.split(' ')[0] || 'there';
      const message = replaceTemplateVariables(
        `Hi {customer_name}, this is {company_name}. I'm wrapping up my current job and will be heading your way shortly. See you soon!`,
        {
          customer_name: firstName,
          company_name: companyName || 'your technician'
        }
      );

      const result = await sendSMS({
        phone: nextJob.customers.phone,
        message,
        customerId: nextJob.customer_id,
        companyId,
        jobId: nextJob.id,
        sentBy: user?.id
      });

      if (result.success) {
        setTextSent(true);
      }
    } catch (error) {
    } finally {
      setSendingText(false);
    }
  };

  const openMaps = () => {
    if (!nextJob?.customers?.address) return;
    const address = encodeURIComponent(nextJob.customers.address);
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${address}`, '_blank');
  };

  const toggleEnabled = async () => {
    const newValue = !isEnabled;
    setIsEnabled(newValue);

    await supabaseData
      .from('companies')
      .update({ smart_assistant_enabled: newValue })
      .eq('id', companyId);

    if (!newValue) {
      setShowModal(false);
    }
  };

  const formatTime = (time) => {
    if (!time) return '';
    const [hours, minutes] = time.split(':').map(Number);
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const hour12 = hours % 12 || 12;
    return `${hour12}:${minutes.toString().padStart(2, '0')} ${ampm}`;
  };

  if (!showModal || !nextJob) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Bell className="w-5 h-5" />
              <span className="font-semibold">Smart Assistant</span>
            </div>
            <button onClick={handleDismiss} className="p-1 hover:bg-white/20 rounded-full transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>
          <p className="text-blue-100 text-sm mt-1">Time to wrap up and head to your next job</p>
        </div>

        {/* Next Job Info */}
        <div className="p-4">
          <div className="bg-gray-50 rounded-xl p-4 mb-4">
            <div className="flex items-center gap-2 text-gray-600 text-sm mb-2">
              <Clock className="w-4 h-4" />
              <span>Next Job at {formatTime(nextJob.scheduled_time)}</span>
            </div>

            <h3 className="font-semibold text-lg text-gray-900 mb-1">
              {nextJob.customers?.name || 'Customer'}
            </h3>

            {nextJob.job_type && (
              <p className="text-gray-600 text-sm mb-2">{nextJob.job_type}</p>
            )}

            {nextJob.customers?.address && (
              <button
                onClick={openMaps}
                className="flex items-center gap-2 text-blue-600 hover:text-blue-700 text-sm mt-2 group"
              >
                <MapPin className="w-4 h-4" />
                <span className="group-hover:underline">{nextJob.customers.address}</span>
                <ChevronRight className="w-4 h-4" />
              </button>
            )}

            {driveTime && (
              <div className="flex items-center gap-2 text-gray-500 text-sm mt-3 pt-3 border-t">
                <Navigation className="w-4 h-4" />
                <span>Estimated drive: {driveTime.text}</span>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="space-y-2">
            <button
              onClick={openMaps}
              className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white py-3 px-4 rounded-xl font-medium hover:bg-blue-700 transition-colors"
            >
              <Navigation className="w-5 h-5" />
              Navigate to Next Job
            </button>

            {nextJob.customers?.phone && (
              <button
                onClick={handleQuickText}
                disabled={sendingText || textSent}
                className={`w-full flex items-center justify-center gap-2 py-3 px-4 rounded-xl font-medium transition-colors ${
                  textSent
                    ? 'bg-green-100 text-green-700'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <MessageSquare className="w-5 h-5" />
                {sendingText ? 'Sending...' : textSent ? 'Message Sent!' : 'Text "On My Way"'}
              </button>
            )}
          </div>

          {/* Settings Link */}
          <div className="mt-4 pt-4 border-t flex items-center justify-between">
            <button
              onClick={toggleEnabled}
              className="flex items-center gap-2 text-gray-500 hover:text-gray-700 text-sm"
            >
              {isEnabled ? <BellOff className="w-4 h-4" /> : <Bell className="w-4 h-4" />}
              <span>{isEnabled ? 'Disable Smart Assistant' : 'Enable Smart Assistant'}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SmartAssistant;
